import Card from 'components/card';
import CreatePricingModal from './components/CreatePricingModal';
import { useDisclosure } from '@chakra-ui/hooks';
import { useEffect, useState } from 'react';
import UpdatePricing from './components/updateReminderModal';
import { deletePricing, getPricingTags, updateSpecificPricing } from 'interfaces/pricing';
import CRMMenu from 'views/crm/list-customers/components/CRMMenu';
import { getProducts, getProductsPurchasePrice } from 'interfaces/products';
import { IoMdTrash } from 'react-icons/io';
import { FiCheck, FiEdit2 } from 'react-icons/fi';

const PricingSettings = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isOpenUpdate,
        onOpen: onOpenUpdate,
        onClose: onCloseUpdate,
    } = useDisclosure();
    const [pricings, setPricings] = useState([]);
    const [selectedPricing, setSelectedPricing] = useState(null);
    const [products, setProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [meta, setMeta] = useState<any>(null);
    const [editingPrice, setEditingPrice] = useState<string | null>(null);
    const [tempPrice, setTempPrice] = useState<number>(0);
    const [marginProducts, setMarginProducts] = useState<any[]>([]);

    const loadPricingsMargins = async (pricingsToLoad: any[]) => {
        try {
            const productIds = pricingsToLoad.map((item: any) => item.productId).filter(Boolean);
            if (productIds.length === 0) return;
            const res = await getProductsPurchasePrice(productIds);
            if (!res || !res.data) return;
            const newMargins = pricingsToLoad.map((item: any) => {
                const product = res.data.find((p: any) => p.id === item.productId);
                return {
                    productId: item.productId,
                    totalPurchasePrice: product?.marginData?.purchasePrice || 0,
                };
            });
            setMarginProducts((prev: any[]) => {
                const prevArray = Array.isArray(prev) ? prev : [];
                const existing = prevArray.filter(
                    (m: any) => !newMargins.some((n: any) => n.productId === m.productId),
                );
                return [...existing, ...newMargins];
            });
        } catch (error) {
            console.error('Erreur lors du chargement des marges:', error);
        }
    };

    const getMarginString = (productId: string, sellPrice: number) => {
        try {
            if (!sellPrice || isNaN(sellPrice) || sellPrice <= 0) return null;
            const marginData = marginProducts.find((m: any) => m.productId === productId);
            if (!marginData) return null;
            const purchasePrice = marginData.totalPurchasePrice;
            if (!purchasePrice || isNaN(purchasePrice) || purchasePrice <= 0) return null;
            const margin = sellPrice - purchasePrice;
            const marginPercent = (margin / sellPrice) * 100;
            if (isNaN(margin) || isNaN(marginPercent)) return null;
            return `Marge : ${margin.toFixed(2).replace(/\.?0+$/, '')} € (${marginPercent.toFixed(1).replace(/\.?0+$/, '')} %)`;
        } catch {
            return null;
        }
    };

    const initPricingTags = async () => {
        setIsLoading(true);
        const res = await getPricingTags(1, 100);
        if (res) {
            setPricings(res.data || []);
            setMeta(res.meta);
            setCurrentPage(1);
            setHasMore(res.meta?.hasNextPage || false);
            if (res.data && res.data.length > 0) loadPricingsMargins(res.data);
        }
        setIsLoading(false);
    };

    const loadMorePricingTags = async () => {
        if (isLoading || !hasMore) return;
        
        setIsLoading(true);
        const nextPage = currentPage + 1;
        const res = await getPricingTags(nextPage, 100);
        
        if (res && res.data) {
            setPricings((prev: any) => [...prev, ...res.data]);
            setCurrentPage(nextPage);
            setHasMore(res.meta?.hasNextPage || false);
            if (res.data.length > 0) loadPricingsMargins(res.data);
        }
        setIsLoading(false);
    };

    const loadAllProducts = async () => {
        const allProducts: any[] = [];
        let page = 1;
        let hasMoreProducts = true;
        
        while (hasMoreProducts) {
            const res = await getProducts(page, 100);
            if (res && res.data) {
                allProducts.push(...res.data);
                hasMoreProducts = res.data.length === 100;
                page++;
            } else {
                hasMoreProducts = false;
            }
        }
        
        setProducts(allProducts);
    };

    const removePricing = async (id: string) => {
        const res = await deletePricing(id);
        if (res)
            setPricings((prevPricings: any) =>
                prevPricings.filter((item: any) => item._id !== id),
            );
    };

    const handleEditPrice = (pricingId: string, currentPrice: number) => {
        setEditingPrice(pricingId);
        setTempPrice(currentPrice);
    };

    const handleSavePrice = async (pricingId: string) => {
        // Find pricing for productId
        const pricing = pricings.find((p: any) => p._id === pricingId);
        if (!pricing) return;
        
        const res = await updateSpecificPricing(pricingId, { 
            price: tempPrice,
            productId: pricing.productId,
            tags: pricing.tags
        });
        if (res) {
            setPricings((prevPricings: any) =>
                prevPricings.map((item: any) =>
                    item._id === pricingId ? { ...item, price: tempPrice } : item
                )
            );
        }
        setEditingPrice(null);
    };

    const handleCancelEdit = () => {
        setEditingPrice(null);
        setTempPrice(0);
    };

    useEffect(() => {
        initPricingTags();
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (
                window.innerHeight + document.documentElement.scrollTop
                >= document.documentElement.offsetHeight - 500
            ) {
                loadMorePricingTags();
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, isLoading, hasMore]);

    // function to group pricings by tags
    const groupPricingsByTags = () => {
        const grouped: any = {};
        pricings.forEach((pricing: any) => {
            const tagKey = pricing.tags.sort().join(',') || 'sans-tag';
            if (!grouped[tagKey]) {
                grouped[tagKey] = {
                    tags: pricing.tags,
                    items: [],
                };
            }
            grouped[tagKey].items.push(pricing);
        });
        return grouped;
    };

    const GroupPricing = (props: { tagKey: string; group: any }) => {
        const { tagKey, group } = props;

        return (
            <div className="mt-1 col-span-2 rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-base font-semibold">
                        {group.tags.length > 0 ? (
                            <>
                                <span className="text-gray-500">Tags:</span>{' '}
                                <span className="text-[#01499b] font-bold">
                                    {group.tags.join(', ')}
                                </span>
                            </>
                        ) : (
                            'Sans tag'
                        )}
                    </h4>
                    <span className="text-xs text-white bg-[#01499b] px-2 py-1 rounded font-medium">
                        {group.items.length} produit{group.items.length > 1 ? 's' : ''}
                    </span>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                    {group.items.map((pricing: any, idx: number) => (
                        <div
                            key={pricing._id}
                            className="flex flex-col p-3 bg-white border border-gray-100 rounded hover:border-[#01499b] hover:bg-blue-50/30 transition"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-800">
                                        {pricing?.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        Ref: {pricing?.ref || 'N/A'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {editingPrice === pricing._id ? (
                                        <>
                                            <input
                                                type="number"
                                                value={tempPrice}
                                                onChange={(e) => setTempPrice(parseFloat(e.target.value) || 0)}
                                                className="w-20 px-2 py-1 text-sm border border-[#01499b] rounded focus:outline-none focus:ring-2 focus:ring-[#01499b] focus:ring-opacity-50"
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleSavePrice(pricing._id);
                                                    } else if (e.key === 'Escape') {
                                                        handleCancelEdit();
                                                    }
                                                }}
                                            />
                                            <span className="text-sm text-gray-600">€</span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSavePrice(pricing._id);
                                                }}
                                                className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition"
                                                title="Enregistrer"
                                            >
                                                <FiCheck size={18} />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCancelEdit();
                                                }}
                                                className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition"
                                                title="Annuler"
                                            >
                                                ×
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-sm font-semibold text-[#01499b] min-w-[60px] text-right">
                                                {pricing.price} €
                                            </p>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditPrice(pricing._id, pricing.price);
                                                }}
                                                className="p-1 text-[#01499b] hover:text-[#013875] hover:bg-blue-50 rounded transition"
                                                title="Modifier le prix"
                                            >
                                                <FiEdit2 size={16} />
                                            </button>
                                        </>
                                    )}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removePricing(pricing._id);
                                        }}
                                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition"
                                        title="Supprimer"
                                    >
                                        <IoMdTrash size={18} />
                                    </button>
                                </div>
                            </div>
                            {(() => {
                                const currentPrice = editingPrice === pricing._id ? tempPrice : pricing.price;
                                const marginStr = getMarginString(pricing.productId, currentPrice);
                                if (!marginStr) return null;
                                const isPositive = !marginStr.includes('-');
                                return (
                                    <p className={`text-xs mt-1 text-right ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                                        {marginStr}
                                    </p>
                                );
                            })()}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const groupedPricings = groupPricingsByTags();

    return (
        <div className="">
            {' '}
            <CRMMenu path="/group-pricing" />
            <Card extra={'w-full mt-5 px-6 py-6 border'}>
                {/* Header */}

                <CreatePricingModal
                    isOpen={isOpen}
                    products={products}
                    onClose={() => {
                        initPricingTags();
                        onClose();
                    }}
                    onOpen={() => {
                        if (products.length === 0) loadAllProducts();
                    }}
                />
                <UpdatePricing
                    isOpen={isOpenUpdate}
                    pricingId={selectedPricing}
                    products={products}
                    onClose={() => {
                        setSelectedPricing(null);
                        initPricingTags();
                        onCloseUpdate();
                    }}
                    onOpen={() => {
                        if (products.length === 0) loadAllProducts();
                    }}
                />
                <div className="w-full px-1 flex justify-between">
                    <div>
                        <h4 className="text-xl font-medium">
                            Tarifs par groupes clients
                        </h4>
                        <p className="mt-1 text-sm">
                            Attribuez des tarifs spécifiques à vos groupes de
                            clients
                        </p>
                    </div>
                    <button
                        className="linear flex h-7 cursor-pointer items-center justify-center rounded-xl border border-[#01499b] px-2 py-2 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100"
                        onClick={onOpen}
                    >
                        Créer un nouveau tarif
                    </button>
                </div>
                {/* inputs */}
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                    {Object.keys(groupedPricings).length > 0 ? (
                        Object.keys(groupedPricings).map((tagKey) => (
                            <GroupPricing
                                key={tagKey}
                                tagKey={tagKey}
                                group={groupedPricings[tagKey]}
                            />
                        ))
                    ) : (
                        <div className="mt-10 col-span-2 flex justify-center">
                            <p className="text-xl text-gray-500">
                                Aucun tarif trouvé
                            </p>
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default PricingSettings;
